/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import request from 'modules/common/utils/request';
import { usersActions } from './slice';
import API from './constants';

/**
 * Users list generator function to get users list
 * @param {*} param0
 */
export function* usersListGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_USER_LIST, payload);
    yield put(usersActions.usersListSucceeded(response));
  } catch (error) {
    yield put(usersActions.usersListFailed(error?.message));
  }
}
/**
 * Admin users generator function to create admin user
 * @param {*} param0
 */
export function* adminUsersGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_USERS_ADMIN, payload);
    yield put(usersActions.createAdminUsersSucceeded(response));
  } catch (error) {
    yield put(usersActions.createAdminUsersFailed(error?.message));
  }
}
/**
 * General users generator function to create general user
 * @param {*} param0
 */
export function* generalUsersGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_USERS_GENERAL, payload);
    yield put(usersActions.createGeneralUsersSucceeded(response));
  } catch (error) {
    yield put(usersActions.createGeneralUsersFailed(error?.message));
  }
}
/**
 * Get hotels list generator function to get hotels list for user
 * @param {*} param0
 */
export function* getHotelsListGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_HOTELS, payload);
    yield put(usersActions.getHotelsListSucceeded(response));
  } catch (error) {
    yield put(usersActions.getHotelsListFailed(error?.message));
  }
}
/**
 * Create users generator function to create user
 * @param {*} param0
 */
export function* createUsersGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_USERS, payload);
    yield put(usersActions.createUsersSucceeded(response));
  } catch (error) {
    yield put(usersActions.createUsersFailed(error?.message));
  }
}
/**
 * Update user generator function to update user
 * @param {*} param0
 */
export function* updateUserGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_USER, payload);
    yield put(usersActions.updateUserSucceeded(response));
  } catch (error) {
    yield put(usersActions.updateUserFailed(error?.message));
  }
}
/**
 * Get roles generator function to get roles
 * @param {*} param0
 */
export function* getRolesGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ROLES, payload);
    yield put(usersActions.getRolesSucceeded(response));
  } catch (error) {
    yield put(usersActions.getRolesFailed(error?.message));
  }
}
/**
 * Update role generator function to update user role
 * @param {*} param0
 */
export function* updateRoleGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_ROLE, payload);
    yield put(usersActions.updateRoleSucceeded(response));
  } catch (error) {
    yield put(usersActions.updateRoleFailed(error?.message));
  }
}
/**
 * Update hotels list generator function to update user hotels list
 * @param {*} param0
 */
export function* updateHotelsListGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_HOTEL, payload);
    yield put(usersActions.updateHotelsListSucceeded(response));
  } catch (error) {
    yield put(usersActions.updateHotelsListFailed(error?.message));
  }
}
/**
 * Delete user generator function to delete user
 * @param {*} param0
 */
export function* deleteUserGenerator({ payload }) {
  try {
    const response = yield call(request, API.DELETE_USER, payload);
    yield put(usersActions.deleteUserSucceeded({ response, data: payload }));
  } catch (error) {
    yield put(usersActions.deleteUserFailed(error?.message));
  }
}
/**
 * All hotels selection list generator function to obtain the list of all registered hotels
 * @param {*} param0
 */
export function* getHotelsSelectionListGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALL_HOTELS, payload);
    yield put(usersActions.getSelectionHotelsListSucceeded(response));
  } catch (error) {
    yield put(usersActions.getSelectionHotelsListFailed(error?.message));
  }
}
/**
 * Get hotels list generator function to get hotels list of logged user
 * @param {*} param0
 */
export function* getLoggedUserHotelsListGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_HOTELS, payload);
    yield put(usersActions.getLoggedUserHotelsListSucceeded(response));
  } catch (error) {
    yield put(usersActions.getLoggedUserHotelsListFailed(error?.message));
  }
}
/**
 * Invite user generator function to invite user for onboarding
 * @param {*} param0
 */
export function* inviteUserGenerator({ payload }) {
  try {
    const response = yield call(request, API.INVITE_USER, payload);
    yield put(usersActions.inviteUserSucceeded(response));
  } catch (error) {
    yield put(usersActions.inviteUserFailed(error?.message));
  }
}
/**
 * Change user password generator function to change general user's password
 * @param {*} param0
 */
export function* changeUserPasswordGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_CHANGE_PASSWORD, payload);
    yield put(usersActions.changeUserPasswordSucceeded(response));
  } catch (error) {
    yield put(usersActions.changeUserPasswordFailed(error?.message));
  }
}
/**
 * Get hotel groups generator function to get all hotel groups
 * @param {*} param0
 */
export function* getAllHotelGroupsGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALL_HOTEL_GROUPS, payload);
    yield put(usersActions.getHotelGroupListSucceeded(response));
  } catch (error) {
    yield put(usersActions.getHotelGroupListFailed(error?.message));
  }
}
/**
 * Assigned hotel groups to user generator function
 * @param {*} param0
 */
export function* assignHotelGroupsToUserGenerator({ payload }) {
  try {
    const response = yield call(request, API.ASSIGN_HOTEL_GROUPS, payload);
    yield put(usersActions.assignHotelGroupsSucceeded(response));
  } catch (error) {
    yield put(usersActions.assignHotelGroupsFailed(error?.message));
  }
}
/**
 * Get assigned hotel groups generator function to get all hotel groups
 * @param {*} param0
 */
export function* getAssignedHotelGroupsGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ASSIGNED_HOTEL_GROUPS, payload);
    yield put(usersActions.getAssignedHotelGroupListSucceeded(response));
  } catch (error) {
    yield put(usersActions.getAssignedHotelGroupListFailed(error?.message));
  }
}
//
export function* userSaga() {
  yield takeLatest(usersActions.usersList.type, usersListGenerator);
  yield takeLatest(usersActions.createAdminUsers.type, adminUsersGenerator);
  yield takeLatest(usersActions.createGeneralUsers.type, generalUsersGenerator);
  yield takeLatest(usersActions.updateUser.type, updateUserGenerator);
  yield takeLatest(usersActions.updateRole.type, updateRoleGenerator);
  yield takeLatest(usersActions.deleteUser.type, deleteUserGenerator);
  yield takeLatest(usersActions.getRoles.type, getRolesGenerator);
  yield takeLatest(usersActions.getHotelsList.type, getHotelsListGenerator);
  yield takeLatest(usersActions.updateHotelsList.type, updateHotelsListGenerator);
  yield takeLatest(usersActions.getSelectionHotelsList.type, getHotelsSelectionListGenerator);
  yield takeLatest(usersActions.getLoggedUserHotelsList.type, getLoggedUserHotelsListGenerator);
  yield takeLatest(usersActions.inviteUser.type, inviteUserGenerator);
  yield takeLatest(usersActions.changeUserPassword.type, changeUserPasswordGenerator);
  yield takeLatest(usersActions.createUsers.type, createUsersGenerator);
  yield takeLatest(usersActions.getHotelGroupList.type, getAllHotelGroupsGenerator);
  yield takeLatest(usersActions.getAssignedHotelGroupList.type, getAssignedHotelGroupsGenerator);
  yield takeLatest(usersActions.assignHotelGroups.type, assignHotelGroupsToUserGenerator);
}
//
export default userSaga;
